import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { hardwareApi, type DeviceModelRow } from '../../services/hardwareApi';
import DeviceEditor from '../pos/hardware/DeviceEditor';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * NEW / EDIT EQUIPMENT, AS A PAGE (2026-08-30).
 *
 * `DeviceEditor` used to open only as a modal over the stock list. It is a long
 * form — identity, kind, lifecycle, specs, app profile, compatibility, vendor,
 * economics — and a 92vh dialog made it scroll inside a scroll. Creating a
 * device is now a route of its own; the same component renders it with
 * `variant="page"`, so there is one form, not two that drift.
 *
 * WHAT THIS PAGE ADDS ON TOP OF THE FORM is the handoff. The form's last
 * question is whether the device is sold to tenants at all, and the two answers
 * lead to two different places:
 *
 *   - stock only  → back to the list. The process ends; a cable needs no terms.
 *   - sellable    → straight into the financing wizard with this device already
 *                   chosen, so selling terms are set while the operator is still
 *                   thinking about the device rather than on a separate trip.
 *
 * Routes: `/inventory/stock/new`, `/inventory/stock/:modelId/edit`, and
 * `/inventory/stock/new?duplicate=<modelId>` for duplicate-as-a-variation.
 */
const DeviceEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { modelId } = useParams<{ modelId: string }>();
  const [searchParams] = useSearchParams();
  const duplicateOf = searchParams.get('duplicate');

  // The editor needs every model for its "replaced by" and compatibility
  // pickers, and to keep a minted Device ID unique.
  const [allModels, setAllModels] = useState<DeviceModelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    hardwareApi.listModels()
      .then((r) => { if (!cancelled) setAllModels(r.items || []); })
      .catch((e: any) => { if (!cancelled) setError(e?.message || 'Failed to load the device catalog'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const backToList = () => navigate('/inventory/stock');

  /**
   * A DELETE also lands here with no payload — `DeviceEditor` calls `onSaved()`
   * bare after deleting, so the absent argument means "nothing to continue to".
   */
  const afterSave = (saved?: { modelId: string; sellable: boolean; terms?: 'new' | 'edit' }) => {
    if (saved?.sellable && saved.modelId) {
      // `back=device` so the wizard's Done returns HERE — the device cannot be
      // published until these terms exist, so the trip has to come back.
      // `edit=` opens the offers this device ALREADY has rather than stacking a
      // second policy on top of them.
      const target = saved.terms === 'edit'
        ? `edit=model:${encodeURIComponent(saved.modelId)}`
        : `target=model:${encodeURIComponent(saved.modelId)}`;
      navigate(`/inventory/financing/new?${target}&back=device`);
      return;
    }
    backToList();
  };

  /**
   * NO PAGE CHROME AT ALL.
   *
   * `InventoryPage` wraps its children in `min-h-screen bg-black -m-3 md:-m-6`
   * — a full-bleed shell that exists to host ITS black header. The editor draws
   * its own header, so that shell contributed only a black band above the bar
   * and a stacked-header look no amount of `bare`/`flush` could flatten,
   * because the band is the wrapper itself.
   *
   * The one thing the shell did that is still needed is cancelling `main`'s
   * `p-3 md:p-6`, so the bar can run edge to edge. That is the single line
   * below; everything else the editor paints.
   */
  const isNew = !modelId;

  /*
   * Exactly the viewport, with the scrolling moved INSIDE.
   *
   * `main` scrolls and carries `p-3 md:p-6`, and a `sticky top-0` header inside
   * it pins against main's PADDING box — which parked the black bar 24px down,
   * the gap. Cancelling that padding with a negative margin does not help,
   * because sticky measures the padding box either way. So the editor takes the
   * whole viewport, hides its own overflow, and scrolls its body: the bar is
   * simply the first row of a column, with no sticky involved.
   */
  return (
    <div className="-m-3 flex h-screen flex-col overflow-hidden bg-stone-100 md:-m-6">
      {loading ? (
        <RubyLoader variant="page" />
      ) : (
        <>
          {error && (
            <div className="border-b border-amber-200 bg-amber-50 px-6 py-2 text-xs text-amber-900">
              <span className="flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                {error} — the form still works, but the pickers that list other devices will be empty.
              </span>
            </div>
          )}
          <DeviceEditor
            variant="page"
            modelId={modelId || null}
            duplicateOf={duplicateOf}
            allModels={allModels}
            onClose={backToList}
            onSaved={afterSave}
          />
        </>
      )}
    </div>
  );
};

export default DeviceEditorPage;
